import { useEffect, useRef, useState } from 'react';
import { Columns } from 'react-feather';
import './ColumnChooser.scss';

export interface ColumnOption {
  id: string;
  label: string;
  locked?: boolean; // always visible, cannot be unchecked
}

interface ColumnChooserProps {
  options: ColumnOption[];
  savedColumns: string[] | null;
  onChange: (columns: string[] | null) => void;
}

/** True when the column should be rendered for the given saved preference. */
export const isColVisible = (
  options: ColumnOption[],
  savedColumns: string[] | null,
  id: string
): boolean => {
  const option = options.find((o) => o.id === id);
  return !!option?.locked || savedColumns == null || savedColumns.includes(id);
};

/** Number of visible columns, for colSpan on loading / empty / detail rows. */
export const visibleColCount = (options: ColumnOption[], savedColumns: string[] | null): number =>
  options.filter((o) => isColVisible(options, savedColumns, o.id)).length;

const ColumnChooser = ({ options, savedColumns, onChange }: ColumnChooserProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const toggle = (id: string) => {
    // Ids saved earlier that no longer exist are dropped here
    const current = options
      .filter((o) => isColVisible(options, savedColumns, o.id))
      .map((o) => o.id);
    const next = current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
    onChange(next);
  };

  return (
    <div className="column-chooser" ref={wrapperRef}>
      <button
        type="button"
        className="btn btn-outline-primary column-chooser-btn"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <Columns size={16} />
        <span>Columns</span>
      </button>
      {open && (
        <div className="column-chooser-menu">
          {options.map((option) => (
            <label key={option.id} className="column-chooser-item">
              <input
                type="checkbox"
                checked={isColVisible(options, savedColumns, option.id)}
                disabled={!!option.locked}
                onChange={() => toggle(option.id)}
              />
              <span>{option.label}</span>
            </label>
          ))}
          {savedColumns !== null && (
            <button
              type="button"
              className="column-chooser-reset"
              onClick={() => onChange(null)}
            >
              Reset to default
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnChooser;
